import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../components/ui/Card.jsx";
import {
  FileText,
  CheckCircle,
  Clock,
  Eye,
  Plus,
  Bell,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ArrowRight,
} from "lucide-react";

import { getTrackApplicationsByApplicant } from "../../api/borrowerPortal/TrackApplications";
import { getBorrowerEmail } from "../../utils/auth";
import { hasStoredApiCredentials, hasAuthenticatedCookieSession } from "../../api/client";
import { getNotifications } from "../../api/lenderPortal/notifications.js";
import { shortlistPathForCategory } from "../../utils/sourcingCategory.js";
import { getStageChipClasses } from "../../utils/applicationStages.js";

/**
 * Notification shape (expected if you pass your own):
 * { id, type, title, body, ts, read, actionHref?, category?, applicationId?, fromStage?, toStage? }
 */
const pickApplicantEmail = () => {
  return getBorrowerEmail() || "test3@example.com";
};

const stripStageWord = (label) =>
  String(label || "")
    .replace(/\s+stage$/i, "")
    .trim();

/** Parse API subject into a structured card (application id shown once). */
const parseBorrowerNotification = (subject, documentName) => {
  const raw = String(subject || "").trim();
  const applicationId = String(documentName || "").trim() || null;

  const movedPatterns = [
    /your application\s+(.+?)\s+has moved(?:\s+from)?:\s*(.+?)\s*(?:→|->)\s*(.+)$/i,
    /your application\s+(.+?)\s+has moved\s+from:\s*(.+?)\s+stage\s*(?:→|->)\s*(.+?)\s*stage$/i,
  ];

  for (const pattern of movedPatterns) {
    const match = raw.match(pattern);
    if (!match) continue;
    const idFromSubject = String(match[1] || "").trim();
    return {
      kind: "stage_move",
      applicationId: applicationId || idFromSubject || null,
      fromStage: stripStageWord(match[2]),
      toStage: stripStageWord(match[3]),
      title: "Stage updated",
      body: null,
    };
  }

  if (!raw) {
    return {
      kind: "generic",
      applicationId,
      fromStage: null,
      toStage: null,
      title: applicationId ? "Application update" : "Application notification",
      body: null,
    };
  }

  // Drop a trailing "Application: ID" echo from subject if present
  let title = raw;
  if (applicationId) {
    title = title
      .replace(new RegExp(`\\b${applicationId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "gi"), "")
      .replace(/\s{2,}/g, " ")
      .replace(/\s+has\s+/i, " has ")
      .trim();
    title = title.replace(/^your application\s+/i, "Your application ").trim();
    if (/^your application\s+has\b/i.test(title)) {
      title = title.replace(/^your application\s+/i, "Your application ");
    }
  }

  return {
    kind: "generic",
    applicationId,
    fromStage: null,
    toStage: null,
    title: title || "Application update",
    body: null,
  };
};

const playNotificationPing = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    const playTone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.12, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + duration + 0.02);
    };

    playTone(880, now, 0.12);
    playTone(1320, now + 0.1, 0.18);
    window.setTimeout(() => {
      ctx.close().catch(() => {});
    }, 450);
  } catch {
    /* ignore autoplay / audio errors */
  }
};

const KPI_TONES = {
  sky: {
    card: "border-sky-400/40 bg-gradient-to-br from-sky-500 to-sky-400 text-white shadow-sm shadow-sky-200/50",
    icon: "bg-white/20 text-white",
    value: "text-white",
    label: "text-white/90",
  },
  emerald: {
    card: "border-emerald-400/40 bg-gradient-to-br from-emerald-600 to-emerald-500 text-white shadow-sm shadow-emerald-200/50",
    icon: "bg-white/20 text-white",
    value: "text-white",
    label: "text-white/90",
  },
  amber: {
    card: "border-amber-400/40 bg-gradient-to-br from-amber-500 to-amber-400 text-white shadow-sm shadow-amber-200/50",
    icon: "bg-white/20 text-white",
    value: "text-white",
    label: "text-white/90",
  },
};

function StatCard({ icon: Icon, tone = "sky", value, label, onClick }) {
  const styles = KPI_TONES[tone] || KPI_TONES.sky;
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={
        `flex flex-1 min-w-0 flex-col items-center rounded-2xl border px-2 py-3 text-center ${styles.card} ` +
        (onClick ? "cursor-pointer hover:brightness-105 active:scale-[0.98] transition-all" : "")
      }
    >
      <span
        className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${styles.icon}`}
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className={`mt-2 text-xl font-bold leading-none ${styles.value}`}>{value}</div>
      <div className={`mt-1 text-[11px] font-medium leading-tight ${styles.label}`}>{label}</div>
    </Comp>
  );
}

export default function Dashboard({
  user,
  applications: applicationsProp,
  notifications,
}) {
  const navigate = useNavigate();

  const [applications, setApplications] = useState(
    Array.isArray(applicationsProp) ? applicationsProp : []
  );
  const [appsLoading, setAppsLoading] = useState(!Array.isArray(applicationsProp));
  const [appsError, setAppsError] = useState("");

  const [authReady, setAuthReady] = React.useState(false);
  React.useEffect(() => {
    const hasAuth =
      localStorage.getItem("ds_isAuthed") === "1" ||
      sessionStorage.getItem("ds_isAuthed") === "1";
    if (hasAuth) {
      const t = setTimeout(() => setAuthReady(true), 250);
      return () => clearTimeout(t);
    }
  }, []);

  const applicantEmail = useMemo(() => {
    return user?.email || pickApplicantEmail();
  }, [user?.email]);

  const loadApplications = async () => {
    setAppsError("");
    setAppsLoading(true);
    try {
      const rows = await getTrackApplicationsByApplicant(applicantEmail, {
        limit: 100,
        orderBy: "modified desc",
        fields: ["name", "status", "modified", "creation"],
      });

      const normalized = (rows || []).map((r) => ({
        id: r.name,
        status: (r.status || "").toLowerCase(),
        modified: r.modified,
        creation: r.creation,
        _raw: r,
      }));

      setApplications(normalized);
    } catch (e) {
      if ((e?.status === 401 || e?.status === 403) && !hasStoredApiCredentials() && !hasAuthenticatedCookieSession()) {
        setAppsError("Your session has expired. Please sign back in.");
      } else if (e?.status === 403) {
        setAppsError("Unable to load applications. You may need the Borrower role on your account — contact support if this continues.");
      } else {
        setAppsError(e?.message || "Failed to load applications.");
      }
      setApplications([]);
    } finally {
      setAppsLoading(false);
    }
  };

  useEffect(() => {
    if (!authReady) return;
    if (Array.isArray(applicationsProp)) {
      setApplications(applicationsProp);
      setAppsLoading(false);
      setAppsError("");
      return;
    }
    loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationsProp, applicantEmail, authReady]);

  const total = applications.length;

  const approved = applications.filter((a) =>
    ["approved", "accepted", "completed"].includes(a.status)
  ).length;

  const pending = applications.filter((a) =>
    ["submitted", "under_review", "pending", "in_progress", "in-progress"].includes(a.status)
  ).length;

  const [allItems, setAllItems] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState("");
  const seenNotifIdsRef = React.useRef(null);

  const loadNotifications = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setNotifLoading(true);
      setNotifError("");
    }
    try {
      const res = await getNotifications({ limit: 20, unread_only: 1 });
      const rows = Array.isArray(res?.notifications)
        ? res.notifications
        : Array.isArray(res)
          ? res
          : [];
      const mapped = rows.map((n) => {
        const parsed = parseBorrowerNotification(n.subject, n.document_name);
        return {
          id: n.name || n.notification_id || `${n.document_name || "notif"}-${n.creation || Date.now()}`,
          type: String(n.type || "").toLowerCase() === "alert" ? "urgent" : "info",
          kind: parsed.kind,
          title: parsed.title,
          body: parsed.body,
          applicationId: parsed.applicationId,
          fromStage: parsed.fromStage,
          toStage: parsed.toStage,
          ts: n.creation ? new Date(n.creation.replace(" ", "T")).getTime() : Date.now(),
          read: Number(n.read || 0) === 1,
          actionHref: parsed.applicationId
            ? `/borrower/applications?app=${encodeURIComponent(parsed.applicationId)}`
            : "/borrower/applications",
          category: "application",
        };
      });

      const nextIds = new Set(mapped.map((n) => n.id));
      if (seenNotifIdsRef.current === null) {
        seenNotifIdsRef.current = nextIds;
      } else {
        const hasNew = [...nextIds].some((id) => !seenNotifIdsRef.current.has(id));
        if (hasNew) playNotificationPing();
        seenNotifIdsRef.current = new Set([...seenNotifIdsRef.current, ...nextIds]);
      }

      setAllItems(mapped);
    } catch (e) {
      if (!silent) {
        setNotifError(e?.message || "Failed to load notifications.");
        setAllItems([]);
      }
    } finally {
      if (!silent) setNotifLoading(false);
    }
  }, []);

  useEffect(() => {
    if (Array.isArray(notifications)) {
      setAllItems(notifications);
      return undefined;
    }
    if (!authReady) return undefined;
    loadNotifications({ silent: false });
    const poll = window.setInterval(() => {
      loadNotifications({ silent: true });
    }, 30000);
    return () => window.clearInterval(poll);
  }, [notifications, authReady, loadNotifications]);

  const isApplicationNotification = (n) =>
    n?.category === "application" ||
    (n?.actionHref || "").includes("/borrower/applications") ||
    n?.kind === "stage_move" ||
    !!n?.applicationId ||
    /application/i.test(n?.title || "") ||
    /application/i.test(n?.body || "");

  const items = useMemo(() => allItems.filter(isApplicationNotification), [allItems]);

  const markAllRead = () =>
    setAllItems((prev) =>
      prev.map((n) => (isApplicationNotification(n) ? { ...n, read: true } : n))
    );
  const markRead = (id) =>
    setAllItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="rounded-2xl bg-sky-500 text-white px-4 py-3 md:px-5 md:py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-bold text-white truncate">
              Welcome back, {user?.name || "Borrower"}!
            </h2>
            <p className="text-xs md:text-sm text-white/90 mt-0.5">
              Manage your mortgage applications efficiently.
            </p>
          </div>
          <button
            onClick={loadApplications}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white hover:bg-white/25 border border-white/20 transition-colors"
            aria-label="Refresh applications"
            title="Refresh applications"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {appsLoading && (
        <div className="rounded-2xl border border-[#e7eaef] bg-white px-4 py-3 text-slate-700 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading applications…
        </div>
      )}
      {!appsLoading && appsError && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">
          {appsError}
        </div>
      )}

      <div className="grid gap-4 md:gap-6 md:grid-cols-[1fr,360px]">
        <div className="space-y-4 md:space-y-6">
          <div className="flex gap-2 md:gap-3">
            <StatCard
              icon={FileText}
              tone="sky"
              value={total}
              label="Total"
              onClick={() => navigate("/borrower/applications")}
            />
            <StatCard
              icon={CheckCircle}
              tone="emerald"
              value={approved}
              label="Approved"
            />
            <StatCard
              icon={Clock}
              tone="amber"
              value={pending}
              label="Pending"
            />
          </div>

          <Card className="p-4 md:p-6">
            <h3 className="text-base md:text-lg font-bold text-slate-900 mb-4">Quick Actions</h3>
            <div className="grid md:grid-cols-2 gap-3 md:gap-4">
              <button
                onClick={() => navigate(shortlistPathForCategory())}
                className="flex items-center justify-center gap-2 bg-sky-500 text-white py-3 md:py-4 rounded-xl font-semibold hover:bg-sky-600 transition"
              >
                <Plus size={20} aria-hidden="true" />
                New Application
              </button>
              <button
                onClick={() => navigate("/borrower/applications")}
                className="flex items-center justify-center gap-2 bg-slate-800 text-white py-3 md:py-4 rounded-xl font-semibold hover:bg-slate-900 transition"
              >
                <Eye size={20} aria-hidden="true" />
                View Applications
              </button>
            </div>
          </Card>
        </div>

        <aside className="h-max md:sticky md:top-4">
          <Card className="p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Bell className="h-5 w-5 text-sky-600 shrink-0" aria-hidden="true" />
                <h2 className="text-slate-900 font-bold text-sm md:text-base truncate">Notifications</h2>
              </div>
              <button
                onClick={markAllRead}
                className="text-xs font-semibold text-sky-600 hover:underline shrink-0"
              >
                Mark all read
              </button>
            </div>

            <div className="mt-3 divide-y divide-[#e7eaef]">
              {notifLoading && (
                <div className="text-sm text-slate-600 p-3 inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading notifications...
                </div>
              )}
              {!notifLoading && notifError && (
                <div className="text-sm text-rose-600 p-3">{notifError}</div>
              )}
              {!notifLoading && !notifError && items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 px-3 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 mb-3">
                    <Bell className="h-6 w-6 text-slate-400" aria-hidden="true" />
                  </div>
                  <p className="text-sm font-semibold text-slate-900">No notifications yet</p>
                  <p className="text-xs text-slate-500 mt-1">Application updates will appear here.</p>
                </div>
              ) : (
                items.map((n) => (
                  <NotificationRow key={n.id} n={n} onRead={() => markRead(n.id)} />
                ))
              )}
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function StageChip({ label }) {
  return (
    <span
      className={`inline-flex max-w-[9.5rem] items-center truncate rounded-full border px-2 py-0.5 text-[11px] font-semibold ${getStageChipClasses(label)}`}
      title={label}
    >
      {label}
    </span>
  );
}

function NotificationRow({ n, onRead }) {
  const isStageMove = n.kind === "stage_move" && n.fromStage && n.toStage;
  const icon = isStageMove ? (
    <RefreshCw className="h-4 w-4 text-sky-600" aria-hidden="true" />
  ) : n.type === "urgent" ? (
    <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
  ) : n.type === "success" ? (
    <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
  ) : (
    <Bell className="h-4 w-4 text-sky-600" aria-hidden="true" />
  );

  return (
    <div className={`py-3 ${n.read ? "opacity-70" : ""}`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-50 border border-slate-200">
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                {isStageMove ? "Stage updated" : n.title}
              </div>
              {n.applicationId ? (
                <div className="mt-0.5 text-[11px] font-medium text-slate-500 truncate" title={n.applicationId}>
                  {n.applicationId}
                </div>
              ) : null}
            </div>
            {!n.read && (
              <button
                onClick={onRead}
                className="text-xs font-semibold text-sky-600 hover:underline shrink-0"
              >
                Mark read
              </button>
            )}
          </div>

          {isStageMove ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <StageChip label={n.fromStage} />
              <ArrowRight className="h-3.5 w-3.5 text-slate-400 shrink-0" aria-hidden="true" />
              <StageChip label={n.toStage} />
            </div>
          ) : n.body ? (
            <div className="mt-1 text-sm text-slate-600">{n.body}</div>
          ) : null}

          {n.actionHref && (
            <a
              href={n.actionHref}
              className="mt-2 inline-block text-xs font-semibold text-sky-600 hover:underline"
            >
              View details
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
